import { produce } from "immer";
import {
  any,
  curry,
  eqBy,
  find,
  findIndex,
  forEach,
  isEmpty,
  none,
  pathSatisfies,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  reduce,
  reject,
  toPairs,
} from "ramda";
import type { Dispatch } from "react";
import { findAppFromStoreByAppName } from "helpers/brand";
import {
  isValidEmail,
  isValidMoney,
  isValidPhoneNumber,
  isValidPincode,
} from "helpers/shared/validators";
import type { AppLocation, IEntity } from "interfaces/app";

import type { ICreateUserResponse } from "types/user";
import type {
  IAppRole,
  IAppUser,
  IAppUserApplications,
  INewUser,
  IUserInfo,
} from "types/user/app_user";

type INewUserErrors = { [index in keyof INewUser | "generic"]?: string };

function validateNewUser(
  user: INewUser,
  rolesSelectedByAppLocation: IRolesSelectedByAppLocation,
  allUsers: IAppUser[],
  pincodes: string[]
): INewUserErrors {
  const errors: INewUserErrors = {};
  const isCreatingPOS = hasRoleForAppLocation(
    "breadcrumb",
    rolesSelectedByAppLocation
  );
  const isCreatingAnalytics = hasRoleForAppLocation(
    "swipely",
    rolesSelectedByAppLocation
  );
  const isCreatingInventory = hasRoleForAppLocation(
    "inventory",
    rolesSelectedByAppLocation
  );

  // Must be creating a user in at least one app:
  if (!isCreatingPOS && !isCreatingAnalytics && !isCreatingInventory) {
    errors.generic = "Please select a role in at least one section.";
    return errors;
  }

  if (!user.firstName) {
    errors.firstName = "First Name is required.";
  }
  if (!user.lastName) {
    errors.lastName = "Last Name is required.";
  }
  if (!isValidPhoneNumber(user.phone || "")) {
    if (user.phone) {
      // Phone must be valid if supplied
      errors.phone = "Please enter a valid phone number.";
    } else if (isCreatingInventory) {
      errors.phone = "Please enter a phone number.";
    }
  }

  // User must be identified by either email address or PIN
  // Email address is required for analytics/inventory
  // At least one of email || pin is required for breadcrumb
  const emailValid = isValidEmail(user.email);
  const pinValid = isValidPincode(user.pincode);
  if (isCreatingPOS) {
    if (pinValid) {
      if (user.pincode && pincodes.includes(user.pincode)) {
        errors.generic = "The POS PIN you have entered is already in use.";
      }
    } else {
      if (!emailValid) {
        errors.generic = "Please enter either a PIN or email for this user.";
      }
      if (user.pincode) {
        errors.pincode = "Please enter a four digit PIN for this user.";
      }
    }
    if (user.nameOnCreditCard && !isValidPincode(user.last4DigitsOnCard)) {
      errors.last4DigitsOnCard =
        "Please enter last four digits of this credit card.";
    }
    if (
      any(
        (role) => !isValidMoney(role.payRate),
        rolesSelectedByAppLocation.breadcrumb
      ) &&
      !errors.generic
    ) {
      errors.generic = "Please enter a pay rate for all selected POS roles.";
    }
  }

  if (!emailValid && (isCreatingAnalytics || isCreatingInventory)) {
    errors.email = "Please enter a valid email address.";
  }
  if (emailValid) {
    const hasEmailCollision = any(
      pipe(
        prop("email"),
        eqBy((email) => email.toLowerCase(), user.email)
      ),
      allUsers
    );
    if (hasEmailCollision) {
      errors.email = "A user with this email already exists.";
    }
  }

  return errors;
}

export const hasRoleForAppLocation = (
  appLocation: AppLocation,
  rolesSelected: IRolesSelectedByAppLocation
) =>
  pathSatisfies(
    (roles: IAppRole[]) => roles.length > 0,
    [appLocation],
    rolesSelected
  );

export const getNewUserAppLocation = (
  appLocation: AppLocation,
  user: INewUser
) => find(propEq("app", appLocation), user.locations || []);

export const getRoleIdsForAppLocation = (
  appLocation: AppLocation,
  user: INewUser
): string[] => {
  const app = getNewUserAppLocation(appLocation, user);
  if (!app || !app.roles.length) {
    return [];
  }
  return pluck<"roleId", string>("roleId", app.roles);
};

export const getStoreKeyForAppLocation = (
  appLocation: AppLocation,
  user: INewUser
): string => {
  const app = getNewUserAppLocation(appLocation, user);
  if (!app) {
    return "";
  }
  return app.storeKey;
};

export const buildUserApplicationsAfterCreate = (
  user: INewUser,
  response: ICreateUserResponse
): IAppUserApplications =>
  reduce(
    (userApplications, [appKey, appCreateStatus]) => {
      if (isCreateUserSuccess(appCreateStatus)) {
        // Build an IUserInfo obj
        const baseUserInfo: IUserInfo = {
          id: propOr(null, "uuid", appCreateStatus),
          firstName: user.firstName,
          lastName: user.lastName,
          roles: getRoleIdsForAppLocation(appKey as AppLocation, user),
        };
        if (appKey === "breadcrumb") {
          userApplications.breadcrumb = [
            {
              ...baseUserInfo,
              pincode: user.pincode,
              storeKey: getStoreKeyForAppLocation("breadcrumb", user),
            },
          ];
        }
        if (appKey === "swipely") {
          userApplications.analytics = [
            {
              ...baseUserInfo,
              storeKey: getStoreKeyForAppLocation("swipely", user),
            },
          ];
        }
        if (appKey === "inventory") {
          // FIXME - correct values here:
          userApplications.inventory = [
            {
              ...baseUserInfo,
              storeKey: "some-inventory-store",
            },
          ];
        }
      }
      return userApplications;
    },
    {} as IAppUserApplications,
    toPairs(response)
  );

function mergeLocationsIntoNewUser(
  draftUser: INewUser,
  rolesSelectedByAppLocation: IRolesSelectedByAppLocation,
  activeStore: IEntity
): INewUser {
  const appsWithRoles = reject(isEmpty, rolesSelectedByAppLocation);
  const locations = toPairs<IAppRole[]>(appsWithRoles).map(
    ([appName, selectedRoles]) => {
      const app = findAppFromStoreByAppName(appName, activeStore);
      return {
        app: appName as AppLocation,
        storeKey: app ? app.storeKey : "",
        roles: selectedRoles,
      };
    }
  );
  return {
    ...draftUser,
    locations,
  };
}

export const isCreateUserSuccess = propEq("status", "success");
export const isCreateUserFail = propEq("status", "failed");

interface IRolesSelectedByAppLocation {
  breadcrumb: IAppRole[];
  swipely: IAppRole[];
  inventory: IAppRole[];
}

// Reducer used by the CreateUser component:

const initialLocationsState: IRolesSelectedByAppLocation = {
  breadcrumb: [],
  swipely: [],
  inventory: [],
};

enum APP_ROLE_ACTION_TYPES {
  CHANGE_ROLE,
  CHANGE_PAY_RATE,
  CHANGE_ZONE,
  CHANGE_IS_DEFAULT,
}

const appRoleReducer = produce(
  (draftState: IRolesSelectedByAppLocation, action: IChangeActions) => {
    // Changing Breadcrumb payRate/zoneId:
    if (action.type === APP_ROLE_ACTION_TYPES.CHANGE_PAY_RATE) {
      const { roleId, payRate } = action;
      const thisRole = find(propEq("roleId", roleId), draftState.breadcrumb);
      if (thisRole) {
        thisRole.payRate = payRate;
      }
    } else if (action.type === APP_ROLE_ACTION_TYPES.CHANGE_ZONE) {
      const { roleId, zoneId } = action;
      const thisRole = find(propEq("roleId", roleId), draftState.breadcrumb);
      if (thisRole) {
        thisRole.zoneId = zoneId;
      }
    } else if (action.type === APP_ROLE_ACTION_TYPES.CHANGE_IS_DEFAULT) {
      const { roleId } = action;
      forEach((thisRole) => {
        thisRole.isDefault = thisRole.roleId === roleId;
      }, draftState.breadcrumb);
    }

    // Adding and removing appRoles:
    if (action.type === APP_ROLE_ACTION_TYPES.CHANGE_ROLE) {
      const { appLocation, roleId } = action;
      if (appLocation === "breadcrumb") {
        // You may have any number of breadcrumb roles assigned,
        // simply toggle in list.
        const existingAppRoleIdx = findIndex(
          propEq("roleId", roleId),
          draftState.breadcrumb
        );
        if (existingAppRoleIdx !== -1) {
          // Remove
          draftState.breadcrumb.splice(existingAppRoleIdx, 1);
          if (
            draftState.breadcrumb.length > 0 &&
            none(propEq("isDefault", true), draftState.breadcrumb)
          ) {
            // Set first item to default if no other roles marked default
            draftState.breadcrumb[0].isDefault = true;
          }
        } else {
          draftState.breadcrumb.push({
            roleId,
            zoneId: "",
            payRate: "",
            // The first added role is marked default, otherwise false
            isDefault: draftState.breadcrumb.length ? false : true,
          });
        }
      }
      if (appLocation === "swipely") {
        // You may only have a single analytics role assigned.
        if (!roleId) {
          draftState.swipely = [];
        } else {
          draftState.swipely = [
            {
              roleId,
              zoneId: "",
              payRate: "",
            },
          ];
        }
      }
      if (appLocation === "inventory") {
        // You may only have a single inventory role assigned.
        // TODO - Handle role hierarchy
        if (isEmpty(draftState.inventory)) {
          draftState.inventory = [
            {
              roleId,
              zoneId: "",
              payRate: "",
            },
          ];
        } else {
          // Empty array to un-check
          draftState.inventory = [];
        }
      }
    }
  }
);

interface IChangeRoleAction {
  appLocation: AppLocation;
  roleId: string;
  type: APP_ROLE_ACTION_TYPES.CHANGE_ROLE;
}

const dispatchAppRoleChange = curry(
  (
    dispatch: Dispatch<IChangeRoleAction>,
    appLocation: AppLocation,
    roleId: string
  ) => {
    dispatch({
      appLocation,
      roleId,
      type: APP_ROLE_ACTION_TYPES.CHANGE_ROLE,
    });
  }
);

interface IChangePayRateAction {
  roleId: string;
  payRate: string;
  type: APP_ROLE_ACTION_TYPES.CHANGE_PAY_RATE;
}

const dispatchPayRateChange = curry(
  (
    dispatch: Dispatch<IChangePayRateAction>,
    roleId: string,
    payRate: string
  ) => {
    dispatch({
      roleId,
      payRate,
      type: APP_ROLE_ACTION_TYPES.CHANGE_PAY_RATE,
    });
  }
);

interface IChangeZoneAction {
  roleId: string;
  zoneId: string;
  type: APP_ROLE_ACTION_TYPES.CHANGE_ZONE;
}

const dispatchZoneChange = curry(
  (dispatch: Dispatch<IChangeZoneAction>, roleId: string, zoneId: string) => {
    dispatch({
      roleId,
      zoneId,
      type: APP_ROLE_ACTION_TYPES.CHANGE_ZONE,
    });
  }
);

interface IChangeIsDefaultAction {
  roleId: string;
  type: APP_ROLE_ACTION_TYPES.CHANGE_IS_DEFAULT;
}
const dispatchIsDefaultChange = curry(
  (dispatch: Dispatch<IChangeIsDefaultAction>, roleId: string) => {
    dispatch({
      roleId,
      type: APP_ROLE_ACTION_TYPES.CHANGE_IS_DEFAULT,
    });
  }
);

type IChangeActions =
  | IChangeRoleAction
  | IChangePayRateAction
  | IChangeZoneAction
  | IChangeIsDefaultAction;

export {
  appRoleReducer,
  dispatchAppRoleChange,
  dispatchPayRateChange,
  dispatchZoneChange,
  dispatchIsDefaultChange,
  IChangeActions,
  IRolesSelectedByAppLocation,
  INewUserErrors,
  initialLocationsState,
  mergeLocationsIntoNewUser,
  validateNewUser,
};
